import React, { useState, useEffect } from "react";
import _ from "lodash";
import { Modal } from "react-bootstrap";
import Dropzone from "react-dropzone";
import Cropper from "react-easy-crop";

import { Button, CloseButton } from "components";
import { WarningUploadLogo } from "components/uploadLogo/warningUploadLogo";
import { getCroppedImg } from "helpers/resizeImage";
import { DocumentLogo } from "models";


import ZoomRange from "./zoomRange";

import UploadImage from "static/images/upload.png";
import styles from "./addLogo.module.scss";

const AddLogoModal = ({
	show,
	onHide,
	handleSubmit,
	title = "Change logo",
	origin,
	logoPath = null,
}) => {
	const emptyFileNameError = "Name the File";
	const [modalValues, setModalValues] = useState(new DocumentLogo());

	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [files, setFiles] = useState([]);

	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);

	useEffect(() => {
		// Needed not to track name change from different places
		if (error === emptyFileNameError && modalValues.name) {
			setError("");
		}
	}, [error, modalValues.name]);

	const onDrop = acceptedFiles => {
		setFiles(
			acceptedFiles.map(file =>
				Object.assign(file, {
					preview: URL.createObjectURL(file),
				}),
			),
		);

		if (acceptedFiles.length > 1) {
			setError("Select One file");
		} else if (acceptedFiles.length <= 0) {
			setError(
				"Unexpected file format. Please upload JPG or PNG file with 500px in size.",
			);
		} else {
			let name = modalValues.name;
			if (!name) {
				// If there's no name - use name without type
				const lastIndexOfDot = (
					acceptedFiles[0].name || ""
				).lastIndexOf(".");
				name =
					lastIndexOfDot > 0
						? acceptedFiles[0].name.substring(0, lastIndexOfDot)
						: acceptedFiles[0].name;
			}

			setError("");
			setModalValues({
				...modalValues,
				file: acceptedFiles[0],
				name: name,
			});
		}
	};

	const handleHide = () => {
		if (!submitting) {
			setModalValues(new Document());
		}
		setError("");
		setFiles([]);
		onHide();
	};

	const handleSubmitForm = async () => {
		if (!submitting && !error) {
			setSubmitting(true);
			await handleSubmit(modalValues);
			setSubmitting(false);

			handleHide();
		}
	};

	useEffect(
		() => () => {
			// Make sure to revoke the data uris to avoid memory leaks
			files.forEach(file => URL.revokeObjectURL(file.preview));
		},
		[files],
	);

	let maxSize = 10485760;

	const handleCropComplete = async (croppedArea, croppedAreaPixels) => {
		const { resizeUrlImage, resizeFile } = await getCroppedImg({
			imageSrc: files[0].preview,
			imageType: modalValues.file.type,
			pixelCrop: croppedAreaPixels,
			resize: {
				width: 100,
				height: 50,
			},
		});

		setModalValues(prevState => {
			const fileCopy = new File([resizeFile], prevState.file.name, {
				type: prevState.file.type,
				lastModified: prevState.file.lastModified,
			});

			fileCopy.preview = resizeUrlImage;

			return {
				...prevState,
				file: fileCopy,
			};
		});
	};

	const isFile = files.length > 0;
	const isAgentUploadFile = origin === "agent";

	useEffect(() => {
		if (isAgentUploadFile && logoPath) {
			setFiles([{ preview: logoPath }]);

			const blob = new Blob([logoPath], { type: "image/png" });
			const file = new File([blob], "editLogo.png", {
				type: "image/png",
			});

			setModalValues({
				file: file,
				name: "editLogo.png",
			});
		}
	}, [isAgentUploadFile, logoPath]);

	const AdditionalInfo = ({ isCrop = true }) => (
		<>
			<div
				className="mt-3 font-weight-light"
				style={{
					fontSize: 16,
					color: "rgba(255, 255, 255, 0.3)",
				}}
			>
				{isCrop && (
					<>
						<p className="mb-0">
							<i>File format should be .png, .jpg</i>
						</p>
						<p className="mb-0">
							<i>Recommended file proportion: 2:1</i>
						</p>
					</>
				)}
			</div>
			<WarningUploadLogo />
		</>
	);

	return (
		<Modal
			show={show}
			onHide={handleHide}
			centered
			animation={true}
			size="lg"
		>
			<Modal.Header>
				<Modal.Title>{title}</Modal.Title>
				<CloseButton onClick={handleHide} />
			</Modal.Header>
			<Modal.Body className="d-flex justify-content-center">
				{isAgentUploadFile && isFile ? (
					<div>
						{files.map(file => (
							<div
								key={file.name}
								className={styles.containerLogo}
							>
								<Cropper
									image={file.preview}
									crop={crop}
									zoom={zoom}
									aspect={2 / 1}
									showGrid={false}
									onCropChange={setCrop}
									onCropComplete={handleCropComplete}
									onZoomChange={setZoom}
									classes={{
										containerClassName:
											styles.containerClassName,
									}}
									objectFit="contain"
								/>
							</div>
						))}
						<ZoomRange zoom={zoom} setZoom={setZoom} />
						<AdditionalInfo isCrop={false} />
					</div>
				) : (
					<Dropzone
						onDrop={onDrop}
						minSize={0}
						maxSize={maxSize}
						disabled={submitting}
						accept="image/jpg, image/jpeg, image/png"
						multiple={false}
					>
						{({ getRootProps, getInputProps, rejectedFiles }) => {
							if (
								rejectedFiles?.length > 0 &&
								rejectedFiles[0]?.size > maxSize
							) {
								setError("File is too large"); // Handling it here as can't pass rejected to onDrop
							} else if (modalValues?.name === "") {
								setError(emptyFileNameError);
							}

							return (
								<div
									{...getRootProps({
										className:
											"d-flex flex-column align-items-center text-center",
									})}
								>
									<input {...getInputProps()} />
									{isFile ? (
										files.map(file => (
											<div key={file.name}>
												<img
													alt={file.name}
													src={file.preview}
													style={{
														maxWidth: 200,
													}}
												/>
											</div>
										))
									) : (
										<>
											<img
												src={
													logoPath
														? logoPath
														: UploadImage
												}
												style={{
													maxWidth: 140,
												}}
												alt=""
											/>
										</>
									)}

									<div
										className="mt-3 font-weight-bold"
										style={{ fontSize: 12 }}
									>
										Click to Choose File
										<br />
										or Drag and Drop to Upload
									</div>
									<AdditionalInfo />
									{error && (
										<span
											className="mt-3"
											style={{
												fontSize: 12,
												color: "rgba(242, 90, 90, 0.7)",
											}}
										>
											{error}
										</span>
									)}
								</div>
							);
						}}
					</Dropzone>
				)}
			</Modal.Body>
			<Modal.Footer className="d-flex justify-content-center">
				<Button
					className="mb-3"
					onClick={handleSubmitForm}
					value="Save"
					disabled={
						submitting || _.isEmpty(modalValues.file) || error
					}
				/>
			</Modal.Footer>
		</Modal>
	);
};

export default AddLogoModal;
